import { cn } from '@/lib/utils';
import { ArrowLeft } from '@music-vine/cadence/icons';
import { Button, Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger, Input, Separator } from '@music-vine/cadence/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { type CreateBoardMode, useBoardsContext } from './context-provider';
import { DrawerHeaderBoards } from './drawer-partials';
import { useCreateBoardHandler } from './hooks/useCreateBoardHandler';
import { getDefaultBoardName } from './utils/get-default-board-name';
interface CreateBoardDrawerProps {
  children?: React.ReactNode;
  mode?: CreateBoardMode;
}
export const CreateBoardDrawer = ({
  mode = 'create_board_with_asset',
  children
}: CreateBoardDrawerProps) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const {
    asset
  } = useBoardsContext();
  const {
    handleCreateBoard,
    isCreatingBoard
  } = useCreateBoardHandler({
    newBoardName: inputValue.trim(),
    mode,
    onCreateBoardEnd: () => {
      setDrawerIsOpen(false);
      setInputValue('');
    }
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const defaultBoardName = useMemo(() => {
    if (asset) {
      return getDefaultBoardName({
        asset
      });
    }
    return 'New board';
  }, [asset]);
  const handleOnOpenChange = useCallback((open: boolean) => {
    setDrawerIsOpen(open);
  }, []);
  return <Drawer onOpenChange={handleOnOpenChange} open={drawerIsOpen} data-sentry-element="Drawer" data-sentry-component="CreateBoardDrawer" data-sentry-source-file="drawer-create-board.tsx">
      <DrawerTrigger asChild data-sentry-element="DrawerTrigger" data-sentry-source-file="drawer-create-board.tsx">{children}</DrawerTrigger>

      <DrawerContent className={cn('h-[70vh] px-6 outline-hidden ring-0 focus:outline-hidden focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0')} onOpenAutoFocus={e => {
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.focus();
        if (asset) {
          setInputValue(defaultBoardName);
        }
      }
    }} data-sentry-element="DrawerContent" data-sentry-source-file="drawer-create-board.tsx">
        <DrawerHeader className="p-0" data-sentry-element="DrawerHeader" data-sentry-source-file="drawer-create-board.tsx">
          <DrawerTitle className="sr-only" data-sentry-element="DrawerTitle" data-sentry-source-file="drawer-create-board.tsx">Create board drawer</DrawerTitle>
          <DrawerDescription className="sr-only" data-sentry-element="DrawerDescription" data-sentry-source-file="drawer-create-board.tsx">
            Drawer to create a new board
          </DrawerDescription>
        </DrawerHeader>
        <div className="flex flex-col items-center justify-center gap-6">
          <div className="relative flex w-full items-center justify-center">
            <Button className="absolute left-0" onClick={() => setDrawerIsOpen(false)} size="icon" variant="transparent" data-sentry-element="Button" data-sentry-source-file="drawer-create-board.tsx">
              <ArrowLeft data-sentry-element="ArrowLeft" data-sentry-source-file="drawer-create-board.tsx" />
            </Button>
            <DrawerHeaderBoards data-sentry-element="DrawerHeaderBoards" data-sentry-source-file="drawer-create-board.tsx">Name your board</DrawerHeaderBoards>
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <Input className={cn('w-full rounded-none border-none text-center', 'dark:bg-gray-900 dark:focus:bg-gray-900 dark:enabled:hover:bg-gray-900',
          // background color on dark mode
          'bg-white focus:bg-white enabled:hover:bg-white' // background color on light mode
          )} maxLength={100} onChange={value => setInputValue(value.target.value)} ref={inputRef} value={inputValue} data-sentry-element="Input" data-sentry-source-file="drawer-create-board.tsx" />
            <Separator className="rounded-xl dark:bg-gray-800" data-sentry-element="Separator" data-sentry-source-file="drawer-create-board.tsx" />
          </div>
          <Button className={cn('w-full font-semibold text-sm transition-[opacity,transform] duration-300 ease-in-out-circ')} disabled={isCreatingBoard || !inputValue.trim()} onClick={handleCreateBoard} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="drawer-create-board.tsx">
            Create
          </Button>
        </div>
      </DrawerContent>
    </Drawer>;
};